// usePreloadActiveWorkspace.js
import { useEffect, useRef } from 'react';

// Context
import { useStartupStatus } from 'context/StartupStatusContext';
import { useUser } from 'context/UserContext';
import { useWorkspace } from 'context/WorkspaceContext';

import {
  getInitialWorkspaceForUser,
  subscribeToWorkspace,
} from 'services/workspaces/WorkspaceReadService';

const getWorkspaceKey = (workspace) => {
  const key = typeof workspace?.key === 'string' ? workspace.key.trim() : '';
  if (key) return key;

  const id = typeof workspace?.id === 'string' ? workspace.id.trim() : '';
  return id || null;
};

const getPreferredWorkspaceKey = (userSettings) => {
  return typeof userSettings?.ACTIVE_WORKSPACE_KEY === 'string'
    ? userSettings.ACTIVE_WORKSPACE_KEY.trim()
    : '';
};

/**
 * Custom hook to load the active workspace from user settings.
 */
const usePreloadActiveWorkspace = () => {

  const {
    isUserSettingsLoading,
    user,
    userSettings,
  } = useUser();
  const { clearStatus, setStatus } = useStartupStatus();

  const { activeWorkspace, setActiveWorkspace } = useWorkspace();
  const activeWorkspaceKeyRef = useRef(null);

  useEffect(() => {
    activeWorkspaceKeyRef.current = getWorkspaceKey(activeWorkspace);
  }, [activeWorkspace]);

  useEffect(() => {
    if (!user?.key) {
      clearStatus('startup.workspaces');
      setActiveWorkspace(null);
      return;
    }

    if (isUserSettingsLoading) {
      setStatus('startup.workspaces', {
        message: 'Waiting for your settings before choosing a workspace…',
        priority: 40,
      });
      return;
    }

    let isDisposed = false;
    let unsubscribeWorkspace = null;
    const existingActiveWorkspaceKey = activeWorkspaceKeyRef.current;
    const preferredWorkspaceKey = getPreferredWorkspaceKey(userSettings) || activeWorkspaceKeyRef.current || '';

    if (existingActiveWorkspaceKey) {
      clearStatus('startup.workspaces');
      unsubscribeWorkspace = subscribeToWorkspace(existingActiveWorkspaceKey, (nextWorkspace) => {
        if (isDisposed) return;
        setActiveWorkspace(nextWorkspace || null);
      });

      return () => {
        isDisposed = true;
        clearStatus('startup.workspaces');
        if (unsubscribeWorkspace) unsubscribeWorkspace();
      };
    }

    setStatus('startup.workspaces', {
      message: 'Loading your workspace…',
      priority: 40,
    });

    getInitialWorkspaceForUser({
      userKey: user.key,
      preferredWorkspaceKey,
    })
      .then((workspace) => {
        if (isDisposed) return;

        setActiveWorkspace(workspace || null);
        clearStatus('startup.workspaces');

        const workspaceKey = getWorkspaceKey(workspace);
        if (!workspaceKey) {
          return;
        }

        unsubscribeWorkspace = subscribeToWorkspace(workspaceKey, (nextWorkspace) => {
          if (isDisposed) return;
          setActiveWorkspace(nextWorkspace || null);
        });
      })
      .catch((error) => {
        if (isDisposed) return;
        console.error('[usePreloadActiveWorkspace] failed to load initial workspace:', error);
        clearStatus('startup.workspaces');
        setActiveWorkspace(null);
      });

    return () => {
      isDisposed = true;
      clearStatus('startup.workspaces');
      if (unsubscribeWorkspace) unsubscribeWorkspace();
    };
  }, [
    activeWorkspace?.key,
    clearStatus,
    isUserSettingsLoading,
    setStatus,
    setActiveWorkspace,
    user?.key,
    userSettings,
  ]);

};

export default usePreloadActiveWorkspace;
